import { useLocation } from 'react-router-dom';
import { SUPPORTED_LANGUAGES, Language } from '@/contexts/LanguageContext';
import { routeTranslations } from '@/utils/localizedRoutes';

const BASE_URL = 'https://prelozim.sk';

export interface HreflangLink {
  hreflang: string;
  href: string;
}

export function useHreflangTags() {
  const location = useLocation();

  // Get the canonical Slovak path from current URL
  const getCanonicalSkPath = (): string => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const firstPart = pathParts[0] as Language;
    
    // Check if first part is a language code
    const isLangPrefix = SUPPORTED_LANGUAGES.some(l => l.code === firstPart);
    
    if (isLangPrefix) {
      // Get the path without language prefix
      const pathWithoutLang = '/' + pathParts.slice(1).join('/');
      const currentLang = firstPart;
      
      // Find the Slovak path that matches this localized path
      for (const [skPath, translations] of Object.entries(routeTranslations)) {
        if (translations[currentLang] === pathWithoutLang) {
          return skPath;
        }
      }
      // If not found in translations, return the path as-is (for dynamic routes)
      return pathWithoutLang || '/';
    }
    
    // Already a Slovak path
    return location.pathname || '/';
  };

  // Get localized path for a specific language
  const getLocalizedPath = (skPath: string, lang: Language): string => {
    const translations = routeTranslations[skPath];
    if (translations) {
      return translations[lang];
    }
    // For paths not in translations (dynamic routes), keep the same path
    return skPath;
  };

  // Map language codes to proper ISO hreflang codes with region
  const getHreflangCode = (langCode: string): string => {
    const hreflangMap: Record<string, string> = {
      sk: 'sk-SK',
      en: 'en-GB',
      de: 'de-DE',
      uk: 'uk-UA',
      ru: 'ru-RU',
      es: 'es-ES',
    };
    return hreflangMap[langCode] || langCode;
  };

  // Generate hreflang links for SEO
  // URL format: https://prelozim.sk (no www, no trailing slash)
  const generateHreflangLinks = (): HreflangLink[] => {
    const skPath = getCanonicalSkPath();
    
    return SUPPORTED_LANGUAGES.map(lang => {
      const localizedPath = getLocalizedPath(skPath, lang.code as Language);
      let href: string;
      
      if (lang.code === 'sk') {
        // Slovak: https://prelozim.sk or https://prelozim.sk/cennik
        href = localizedPath === '/' ? BASE_URL : `${BASE_URL}${localizedPath}`;
      } else {
        // Other languages: https://prelozim.sk/en or https://prelozim.sk/en/pricing
        href = localizedPath === '/' 
          ? `${BASE_URL}/${lang.code}` 
          : `${BASE_URL}/${lang.code}${localizedPath}`;
      }
      
      return {
        hreflang: getHreflangCode(lang.code),
        href
      };
    });
  };

  // Get canonical URL for current language
  // URL format: https://prelozim.sk (no www, no trailing slash)
  const getCanonicalUrl = (currentLang: Language): string => {
    const skPath = getCanonicalSkPath();
    const localizedPath = getLocalizedPath(skPath, currentLang);
    
    if (currentLang === 'sk') {
      return localizedPath === '/' ? BASE_URL : `${BASE_URL}${localizedPath}`;
    }
    return localizedPath === '/' 
      ? `${BASE_URL}/${currentLang}` 
      : `${BASE_URL}/${currentLang}${localizedPath}`;
  };

  // Get x-default URL (Slovak version)
  const getDefaultUrl = (): string => {
    const skPath = getCanonicalSkPath();
    return skPath === '/' ? BASE_URL : `${BASE_URL}${skPath}`;
  };

  // Get clean path (Slovak canonical path)
  const getCleanPath = (): string => {
    return getCanonicalSkPath();
  };

  return {
    generateHreflangLinks,
    getCanonicalUrl,
    getCleanPath,
    getDefaultUrl
  };
}
